/* eslint-disable react/prop-types */
import { FC, MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { ListingNew } from '../../../model/Listing';
import { ROUTES, ALTERNATE_PRICE_OPTION } from '../../../constants/data';
import { PriceState } from '../../../context/PriceProvider';
import { formatNumber } from '../../../i18n/format';
import { ConvertToDoge, convertToUsd } from '../../../util/GetDoge';
import PhotoFill from '../PhotoFill';
import { getListingPlaceholder } from '../../../util/listingPlaceholder';
import LikeIcon from '../../icons/LikeButton';
import DisLikeIcon from '../../icons/DisLikeIcon';
import { useLocale } from '../../../i18n/locale';
import { localizePath } from '../../../i18n/localePath';
import { FormattedMessage, useIntl } from 'react-intl';

/**
 * Compact storefront item card for the storefront grid (the redesign). Shows the
 * photo, name, Doge price with the dollar value beside it, condition, and a Ships
 * / Local pickup tag. Sold and out-of-stock items are dimmed with a badge (the
 * grid sorts them after the buyable ones). Deliberately separate from the shared
 * StorefrontItemCard so the map / dashboard / saved / archive cards are untouched.
 */

type Props = {
  listing: ListingNew;
  onSave?: (id: number | string, type: string, like: boolean, savedListingId?: number) => void;
};

const StorefrontGridCard: FC<Props> = ({ listing, onSave }) => {
  const intl = useIntl();
  const { locale } = useLocale();
  const rate = Number(PriceState()?.dogePrice) || 0;

  const id = listing?.id;
  const price = listing?.price;
  const alt = price?.alternatePriceOptions;
  const isDogeDoge = alt === ALTERNATE_PRICE_OPTION.DOGE_DOGE;
  const isNegotiable = alt === ALTERNATE_PRICE_OPTION.NEGOTIABLE;
  const usdNumber = Number(price?.priceInUSD) || 0;

  const doge = isDogeDoge ? Number(price?.priceInDoge) || 0 : ConvertToDoge(usdNumber, rate);
  const usd = isDogeDoge
    ? convertToUsd(Number(price?.priceInDoge) || 0, rate)
    : usdNumber.toFixed(2);

  const outOfStock =
    !listing?.unlimitedStock && listing?.quantity != null && Number(listing?.quantity) <= 0;
  const unavailable = !!listing?.isSold || outOfStock || (listing as any)?.available === false;
  const badge = listing?.isSold
    ? intl.formatMessage({ id: 'shared.storefront_grid_card.sold', defaultMessage: 'Sold' })
    : unavailable
    ? intl.formatMessage({
        id: 'shared.storefront_grid_card.out_of_stock',
        defaultMessage: 'Out of stock'
      })
    : '';

  const condition = (listing as any)?.item_condition?.name as string | undefined;

  const saved = !!(listing?.saved_listings && listing?.saved_listings.length > 0);
  const savedId = saved ? Number(listing?.saved_listings?.[0]?.id) : undefined;

  const onHeart = (e: MouseEvent) => {
    // Both lines matter now that the card is a real <a>: stopPropagation for
    // React handlers, preventDefault so the browser does not follow the href.
    e.preventDefault();
    e.stopPropagation();
    onSave && id != null && onSave(id, 'storefrontItem', saved, savedId);
  };

  // A real link, not an onClick navigate (live-audit fix): crawlers could not
  // follow shop -> item at all, which left every item page a crawl orphan -
  // and people could not ctrl/middle-click a card into a new tab. The id-less
  // degenerate case keeps the old do-nothing behavior.
  const to =
    id != null ? localizePath(`${ROUTES.STOREFRONT_ITEM_DETAILS_ROUTE}/${id}`, locale) : undefined;

  return (
    <Link
      to={to || '#'}
      onClick={(e) => {
        if (!to) e.preventDefault();
      }}
      className="block cursor-pointer overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xs transition-shadow hover:shadow-md">
      <div className="relative w-full aspect-square bg-gray-50">
        <PhotoFill
          alt={
            listing?.name ||
            intl.formatMessage({
              id: 'shared.storefront_grid_card.item_alt',
              defaultMessage: 'item'
            })
          }
          src={listing?.image?.[0]?.url || getListingPlaceholder('storefrontItem')}
          className={clsx('w-full h-full', { 'opacity-50': unavailable })}></PhotoFill>
        <button
          type="button"
          onClick={onHeart}
          aria-label={
            saved
              ? intl.formatMessage({
                  id: 'shared.storefront_grid_card.unsave_item',
                  defaultMessage: 'Unsave item'
                })
              : intl.formatMessage({
                  id: 'shared.storefront_grid_card.save_item',
                  defaultMessage: 'Save item'
                })
          }
          className="absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white">
          {saved ? <DisLikeIcon /> : <LikeIcon />}
        </button>
        {badge ? (
          <span
            className={clsx(
              'absolute top-2 left-2 rounded-full px-2 py-0.5 text-xs font-semibold',
              {
                'bg-gray-900 text-white': listing?.isSold,
                'bg-yellow-100 text-yellow-700': !listing?.isSold
              }
            )}>
            {badge}
          </span>
        ) : null}
      </div>
      <div className="p-3">
        <div className="clamp-2 text-sm font-medium text-gray-900">{listing?.name}</div>
        <div className="mt-1 text-sm">
          {isNegotiable || !usdNumber ? (
            <span className="font-medium text-gray-700">
              <FormattedMessage
                id="shared.storefront_grid_card.negotiable"
                defaultMessage="Negotiable"
              />
            </span>
          ) : (
            <>
              <span className="font-semibold text-yellow-600">
                {'Đ ' + formatNumber(Math.round(Number(doge)))}
              </span>
              <span className="ms-1 text-xs text-gray-500">{'$' + usd}</span>
            </>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
          {condition ? <span>{condition}</span> : null}
          {condition ? <span aria-hidden="true">·</span> : null}
          <span className={listing?.shippable ? 'text-green-600' : ''}>
            {listing?.shippable ? (
              <FormattedMessage id="shared.storefront_grid_card.ships" defaultMessage="Ships" />
            ) : (
              <FormattedMessage
                id="shared.storefront_grid_card.local_pickup"
                defaultMessage="Local pickup"
              />
            )}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default StorefrontGridCard;
